/*
 * UsageBar: used of total (hosting accounts of capacity, storage). Turns warm at 80% and red at 100%.
 *   <UsageBar used={server.accountsUsed} total={server.capacity} label={t('servers:accounts')} />
 */
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'
import { useFormat } from '@/i18n/useFormat'

export function UsageBar({
  used,
  total,
  label,
  showValue = true,
  size = 'md',
  className,
  warnAt = 0.8,
}: {
  used: number
  total: number
  label?: ReactNode
  showValue?: boolean
  size?: 'sm' | 'md'
  className?: string
  warnAt?: number
}) {
  const { t } = useTranslation('common')
  const f = useFormat()
  const ratio = total > 0 ? used / total : 0
  const tone = ratio >= 1 ? 'bg-danger-600' : ratio >= warnAt ? 'bg-warm-500' : 'bg-brand-600'
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label || showValue ? (
        <div className="flex items-baseline justify-between gap-3 text-13">
          {label ? <span className="text-muted">{label}</span> : <span />}
          {showValue ? (
            <span className="figures text-ink">
              {t('ui.usage.ofTotal', { used: f.number(used), total: f.number(total) })}
            </span>
          ) : null}
        </div>
      ) : null}
      <div
        role="meter"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={used}
        aria-label={typeof label === 'string' ? label : undefined}
        className={cn('overflow-hidden rounded-full bg-control-strong', size === 'sm' ? 'h-1.5' : 'h-2')}
      >
        <div
          className={cn('h-full rounded-full transition-[width] duration-500 ease-out', tone)}
          style={{ width: `${Math.min(100, Math.round(ratio * 100))}%` }}
        />
      </div>
    </div>
  )
}
